import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getLabelsets } from '../../services/assetServices';
import {
  createUser,
  editUser,
  getUser,
  toQualificationWrite,
  UserRoleType,
  type IUserQualification,
} from '../../services/userService';
import { IGroup, IGroupInUser, getGroups } from '../../services/groupService';
import { v4 as uuid } from 'uuid';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { Text } from '@fluentui/react';
import { EditItem } from '@moko/ui';
import OtherInfo from '../../common/OtherInfoPortal';
import GroupSelector from './GroupSelector';
import CertificationSelector from './CertificationSelector';
import { getTenant } from '../../services/tenantService';
import { getTenantsAdapter } from '../../common/selectors/TenantSelector';

const USER_ROLE_TYPES: Record<string, UserRoleType> = {
  'FMSManager.Access': UserRoleType.FMSManager,
  'FMSClient.Access': UserRoleType.FMSClient,
  'FMSContractor.Access': UserRoleType.FMSContractor,
  'FMSPortal.AdminAccess': UserRoleType.FMSPortalAdmin,
};

const USER_ROLES: { key: string; value: string }[] = [
  {
    key: 'FMSManager.Access',
    value: 'Manager',
  },
  {
    key: 'FMSClient.Access',
    value: 'Client',
  },
  {
    key: 'FMSContractor.Access',
    value: 'Contractor',
  },
  {
    key: 'FMSPortal.AdminAccess',
    value: 'Portal Admin',
  },
];

const roleKeyFromValue = (value: unknown): string | undefined => {
  if (value == null || value === '') return undefined;
  const raw = String(value).trim();
  return USER_ROLES.find((role) => role.key.toLowerCase() === raw.toLowerCase())
    ?.key;
};

const parseRoles = (value: unknown): string[] => {
  const raw = Array.isArray(value)
    ? value
    : typeof value === 'string'
    ? value.split('|')
    : [];
  const roles: string[] = [];
  for (const item of raw) {
    const key = roleKeyFromValue(item);
    if (!key || roles.includes(key)) continue;
    roles.push(key);
  }
  return roles;
};

const rolesToLookupValue = (roles: string[]) => roles.join('|');

const pickDefaultRole = (
  roles: string[],
  value: unknown
): string | undefined => {
  const key = roleKeyFromValue(value);
  if (key && roles.includes(key)) return key;
  return roles[0];
};

const User = () => {
  const isSaved = useRef(false);
  // Provisioning fields only apply when creating a user (no ?id= in the route).
  const isCreate = !new URLSearchParams(useLocation().search).get('id');

  const setIsInProgress = useSetAtom(isInProgressAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [labelSets, setLabelSets] = useState<string[]>([]);
  const [userOtherInfo, setUserOtherInfo] = useState<string>('{}');
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [qualifications, setQualifications] = useState<IUserQualification[]>(
    []
  );
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<
    number | undefined
  >();
  const [selectedTenantName, setSelectedTenantName] = useState<string>();

  useEffect(() => {
    const fetchData = async () => {
      const abortController = new AbortController();
      setIsInProgress(true);
      try {
        setGroups((await getGroups(abortController, 9999, 1)).items);
        setLabelSets(await getLabelsets(abortController));
      } catch (error: any) {
        console.error('Error:', error);
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
      return () => {
        abortController.abort();
      };
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (isCreate) setQualifications([]);
  }, [isCreate]);

  return (
    <>
      <div>User</div>
      <EditItem
        getAction={async (abortController, id) => {
          const user: any = await getUser(abortController, id);
          const roles = parseRoles(user.roles);
          const defaultRole = pickDefaultRole(roles, user.defaultRole);
          setSelectedRoles(roles);
          setSelectedGroupIds(
            (user.groups ?? []).map((group: IGroupInUser) => group.id)
          );
          setQualifications(
            Array.isArray(user.qualifications) ? user.qualifications : []
          );
          user.roles = rolesToLookupValue(roles);
          user.defaultRole = defaultRole ?? '';
          user.defaultTenant = (user.defaultTenant ?? '').toString();
          const tenantId = user.defaultTenant
            ? Number.parseInt(user.defaultTenant, 10)
            : undefined;
          setSelectedTenantId(tenantId);
          if (tenantId) {
            try {
              const tenant = await getTenant(abortController, tenantId);
              setSelectedTenantName(tenant.name);
            } catch {
              setSelectedTenantName(undefined);
            }
          }
          setUserOtherInfo(user.otherInfo || '{}');
          return user;
        }}
        newAction={async (abortController, item) => {
          const newUser = item as Record<string, string>;
          const provisionIdentity = newUser.provisionIdentity === 'true';
          const roles = parseRoles(newUser.roles);
          const defaultRole = pickDefaultRole(roles, newUser.defaultRole);
          const newUserResponse = createUser(abortController, {
            username: newUser.username,
            firstName: newUser.firstName,
            lastName: newUser.lastName,
            email: newUser.email,
            groupsId: selectedGroupIds,
            defaultTenantId: Number.parseInt(newUser.defaultTenant, 10) || 0,
            otherInfo: userOtherInfo,
            ...(qualifications.length > 0 && {
              qualifications: qualifications.map(toQualificationWrite),
            }),
            roles: roles.map((role) => USER_ROLE_TYPES[role]),
            defaultRole:
              defaultRole === undefined
                ? undefined
                : USER_ROLE_TYPES[defaultRole],
            provisionIdentity,
            isExternalUser:
              provisionIdentity && newUser.isExternalUser === 'true',
            ...(provisionIdentity && {
              identityPassword: newUser.identityPassword,
            }),
          });
          setSuccessMessage(
            `User with id: ${(await newUserResponse).userId} created.`
          );
        }}
        editAction={async (abortController, id, item) => {
          const editedUser = item as Record<string, string>;
          const roles = parseRoles(editedUser.roles);
          const defaultRole = pickDefaultRole(roles, editedUser.defaultRole);
          try {
            await editUser(abortController, id, {
              username: editedUser.username,
              firstName: editedUser.firstName,
              lastName: editedUser.lastName,
              email: editedUser.email,
              groupsId: selectedGroupIds,
              defaultTenantId:
                Number.parseInt(editedUser.defaultTenant, 10) || 0,
              otherInfo: userOtherInfo,
              roles,
              defaultRole,
              qualifications: qualifications.map(toQualificationWrite),
            });
            setSuccessMessage(`User with id: ${id} successfully updated.`);
          } catch (error: any) {
            setErrorMessage(error.message);
          }
        }}
        isSaved={isSaved}
        afterChange={(next) => {
          setSelectedRoles(parseRoles(next.roles));
        }}
        onFetchTenants={getTenantsAdapter}
        back={'/security?tab=users'}
        defaultValues={JSON.stringify({ uuid: uuid() })}
        metadata={{
          fields: [
            {
              name: 'username',
              fieldType: 'String',
              label: 'User name',
            },
            {
              name: 'firstName',
              fieldType: 'String',
              label: 'First name',
            },
            {
              name: 'lastName',
              fieldType: 'String',
              label: 'Last name',
            },
            {
              name: 'email',
              fieldType: 'String',
              label: 'Email',
            },
            {
              name: 'defaultTenant',
              fieldType: 'TenantSelector',
              label: 'Default tenant (optional)',
              lookupList: 'Tenants',
              mandatory: false,
            },
            {
              name: 'roles',
              fieldType: 'Lookup',
              label: 'Roles',
              multiSelect: true,
              lookupList: 'Roles',
            },
            {
              name: 'defaultRole',
              fieldType: 'Lookup',
              label: 'Default role',
              lookupList: 'DefaultRoles',
            },
            ...(isCreate
              ? [
                  {
                    name: 'provisionIdentity',
                    fieldType: 'Checkbox',
                    label: 'Provision identity',
                  },
                  {
                    name: 'identityPassword',
                    fieldType: 'Password',
                    label: 'Identity password',
                    effect: 'Hide',
                  },
                  {
                    name: 'isExternalUser',
                    fieldType: 'Checkbox',
                    label: 'External user',
                    effect: 'Hide',
                  },
                ]
              : []),
          ],
          lookups: [
            {
              name: 'Tenants',
              values:
                selectedTenantId != null && selectedTenantName
                  ? [
                      {
                        key: String(selectedTenantId),
                        value: selectedTenantName,
                      },
                    ]
                  : [],
            },
            {
              name: 'Roles',
              values: USER_ROLES,
            },
            {
              name: 'DefaultRoles',
              values: USER_ROLES.filter((role) =>
                selectedRoles.includes(role.key)
              ),
            },
          ],
          validations: [],
          interactions: isCreate
            ? [
                {
                  name: 'showIdentityFields',
                  description:
                    'Reveal identity password and external-user flag only when provisioning an identity.',
                  condition: {
                    combinator: 'And',
                    not: false,
                    rules: [
                      {
                        field: 'provisionIdentity',
                        operator: '=',
                        value: 'true',
                      },
                    ],
                  },
                  effects: [
                    {
                      fields: ['identityPassword', 'isExternalUser'],
                      effectType: 'Show',
                    },
                  ],
                },
              ]
            : [],
        }}
      />
      <GroupSelector
        groups={groups}
        selectedIds={selectedGroupIds}
        onChange={setSelectedGroupIds}
        label="Groups"
        mandatory
      />
      <CertificationSelector
        qualifications={qualifications}
        onChange={setQualifications}
      />
      <Text variant="xLarge">OtherInfo</Text>
      <OtherInfo
        metadata={{
          fields: [
            {
              name: 'mobile',
              fieldType: 'String',
              label: 'Mobile',
            },
            {
              name: 'LabelSet',
              fieldType: 'Lookup',
              label: 'Label set',
              lookupList: 'labelSets',
            },
          ],
          lookups: [
            {
              name: 'labelSets',
              values: labelSets.map((labelSet) => ({
                key: labelSet,
                value: labelSet,
              })),
            },
          ],
          validations: [],
          interactions: [],
        }}
        isSaved={isSaved}
        onOtherInfoChanged={(newValue: string) => {
          setUserOtherInfo(JSON.stringify(newValue));
        }}
        hideTitle
        otherInfo={userOtherInfo}
      />
    </>
  );
};

export default User;
